import { apiErrorText } from "../../api/api-error.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText } from "../../i18n/portal.js";
/**
 * The phone test as the test panel's third mode draws it (design sync 2026-10-09, `WeTalk.dc.html`
 * "Test chat" / "Test console": Text · Browser voice · My phone | Call me).
 *
 * ONE hook for both phone directions, so the panel's two layouts cannot drift:
 *
 *   * **My phone** — an agent that answers calls. The panel shows the agent's number to ring and the
 *     tester's own phone; there is no Start button in the design, so the panel ARMS the tester's number
 *     by itself once it reads as a phone number (`i_call_the_agent`: the next call from it to this agent
 *     within the window is written `is_test`), and arms it again when the window passes unused while the
 *     page is in view, or when a test call has ended. Arming is free; only the call is billed.
 *   * **Call me** — an agent that makes calls (a survey). The runtime rings the tester's phone from the
 *     agent's own number (`agent_calls_me`) in the language chosen.
 *
 * The call's progress is read from `readVoiceTestCall` until it ends and its live transcript comes over
 * the conversation's stream — the same reads `VoiceTestCallForm` uses (review #125, #172, #233).
 *
 * AMENDED 2026-10-09 (plan-015 TC1, G23, owner answer 18, design §28.5.13): **My phone keeps its arm while
 * the panel is open, and drops it when the panel closes.** The arm's window comes from the arm's own answer
 * (`waitingUntil − waitingSince`: ten minutes in production; the console holds no setting of its own). The
 * hook renews it at half the window (`waitingUntil − now − window/2`), moving `waitingUntil`; a renewal that
 * finds no arm (`test_call_arm_not_found`) reads the call once — a call may just have taken the arm — and
 * otherwise lets the window lapse, so the panel arms afresh. The arm is released when the panel unmounts
 * (closed, another mode, another agent), on `pagehide`, and when a different number is armed in its place.
 * A call after a release is an ordinary conversation. The waiting window's end is the arm's (or the ring's)
 * own `waitingUntil` — the progress read does not carry one — so "the window passed" is said on time.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { useEffect, useRef, useState } from "react";

import { useGateway } from "../../api/provider.js";
import { TestCallUnavailableError, type SurveyArmView } from "../../api/slice/test-call.js";
import type { ConsoleGateway } from "../../api/gateway.js";
import type {
  StreamSubscription,
  TestCallDirection,
  TestCallView,
  TranscriptLineView,
  VoiceAgentView,
} from "../../api/gateway.js";
import { normalisedPhone } from "./voice-test-form.js";
import { voiceAgentPassFrameOf, type VoiceAgentPassArrival } from "../../api/voice-agent-stint-view.js";
import { CONFIRMATION_CODE_SHOWN_EVENT, consoleCodeOf } from "../../api/party-confirmation-view.js";

/** How often an armed or ringing test call's progress is read. */
const READ_INTERVAL_MS = 2_000;
const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * When to renew an arm, in ms from `nowMs`: half a window before it ends (owner answer 18), at once when
 * that moment has passed. `null` when the window cannot be read.
 */
export function renewDelayOf(waitingSince: string, waitingUntil: string, nowMs: number): number | null {
  const since = Date.parse(waitingSince);
  const until = Date.parse(waitingUntil);
  if (!Number.isFinite(since) || !Number.isFinite(until) || until <= since) return null;
  return Math.max(0, until - nowMs - (until - since) / 2);
}

/** Drops an arm, best effort: an arm that is not released lapses at the end of its own window. */
function releaseArm(gateway: ConsoleGateway, voiceAgentId: string, partyNumber: string): void {
  gateway.releaseVoiceTestCallArm(voiceAgentId, partyNumber).catch(() => undefined);
}

/** Whether what was typed is a phone number with its country code, once separators are removed. */
export function isE164(raw: string): boolean {
  return E164.test(normalisedPhone(raw));
}

export type PhoneTestCallState = "idle" | "waiting" | "dialing" | "live" | "ended";

export interface PhoneTestCallControl {
  readonly state: PhoneTestCallState;
  /** The latest read of the call (or the arm), `null` before one. */
  readonly view: TestCallView | null;
  /** The direction of the arm or call in progress, `null` before one. */
  readonly direction: TestCallDirection | null;
  /** The number the current arm or call is for (E.164), `null` before one. */
  readonly partyNumber: string | null;
  readonly conversationId: string | null;
  /** The live transcript, or the read's transcript before the stream has said anything. */
  readonly line: readonly TranscriptLineView[];
  /**
   * plan-021 (design §33.13.3): the passes heard on the stream, each with the line count it arrived after — empty when
   * `line` is the read's (whose lines carry their stints) or nothing was passed.
   */
  readonly voiceAgentPassArrival: readonly VoiceAgentPassArrival[];
  /**
   * plan-029 WAPP-HIST2 (design §40.19.5, ED-17): this call's latest confirmation code, shown instead of being sent
   * (`confirmation_code_shown`, relayed to the person who armed or rang the test only); `null` before one. Never stored.
   */
  readonly consoleCode: string | null;
  /** The window passed and no call came (or the ringing never started). */
  readonly expired: boolean;
  /** The last test call that ended, kept while the next arm waits. */
  readonly lastEnded: TestCallView | null;
  /** A failure, in words (red). */
  readonly message: string | null;
  /** "Not possible right now" — a fact about the platform (neutral). */
  readonly unavailable: string | null;
  readonly pending: boolean;
  /** The number the last start was refused for (E.164), so the panel does not arm it again by itself. */
  readonly refusedFor: string | null;
  /** `surveyArm` (plan-015 TC2, G21): a two-group survey's question group, sent with the start. */
  readonly start: (partyNumber: string, direction: TestCallDirection, languageCode: string, surveyArm?: SurveyArmView) => void;
  readonly hangUp: () => void;
}

export function usePhoneTestCall(voiceAgent: VoiceAgentView): PhoneTestCallControl {
  const gateway = useGateway();
  const [placed, setPlaced] = useState<{
    readonly view: TestCallView;
    readonly partyNumber: string;
    readonly direction: TestCallDirection;
  } | null>(null);
  /** plan-015 TC1: the arm's (or the ring's) window end, moved by each renewal. */
  const [waitingUntil, setWaitingUntil] = useState<string | null>(null);
  /** The streamed lines, and the conversation they belong to. */
  const [stream, setStream] = useState<{
    readonly conversationId: string;
    readonly line: readonly TranscriptLineView[];
    /** plan-021: the passes heard on the same stream, placed after the lines heard before them. */
    readonly arrival?: readonly VoiceAgentPassArrival[];
    /** plan-029: the latest confirmation code shown on this stream. */
    readonly code?: string;
  } | null>(null);
  const [message, setMessage] = usePortalMessage();
  const [unavailable, setUnavailable] = usePortalMessage();
  const [expired, setExpired] = useState(false);
  const [lastEnded, setLastEnded] = useState<TestCallView | null>(null);
  const [refusedFor, setRefusedFor] = useState<string | null>(null);

  const subscription = useRef<StreamSubscription | null>(null);
  const subscribedTo = useRef<string | null>(null);
  useEffect(
    () => () => {
      subscription.current?.close();
      subscription.current = null;
    },
    [],
  );

  const waitingSince = placed?.view.waitingSince ?? null;
  const progress = useQuery({
    queryKey: ["voiceTestCall", voiceAgent.voiceAgentId, placed?.partyNumber ?? "", waitingSince ?? ""],
    queryFn: () => gateway.readVoiceTestCall(voiceAgent.voiceAgentId, placed?.partyNumber ?? "", waitingSince ?? ""),
    enabled: placed !== null && waitingSince !== null && !expired,
    refetchInterval: (query) => (query.state.data?.state === "ended" ? false : READ_INTERVAL_MS),
  });

  const view: TestCallView | null = progress.data ?? placed?.view ?? null;
  const ringing = placed?.direction === "agent_calls_me";
  const state: PhoneTestCallState =
    view === null || placed === null
      ? "idle"
      : view.state === "idle"
        ? expired
          ? "idle"
          : ringing
            ? "dialing"
            : "waiting"
        : view.state === "dialing"
          ? "dialing"
          : view.state === "live"
            ? "live"
            : "ended";

  // The window passed with no call: stop reading. The end is the arm's (renewed) or the ring's own.
  const waiting = placed !== null && view !== null && view.state === "idle" && !expired;
  useEffect(() => {
    if (!waiting || waitingUntil === null) return;
    const timer = setTimeout(
      () => {
        setExpired(true);
      },
      Math.max(0, Date.parse(waitingUntil) - Date.now()),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [waiting, waitingUntil]);

  // plan-015 TC1: My phone renews its arm at half the window while the panel is open.
  const armed = waiting && placed.direction === "i_call_the_agent" ? placed : null;
  const renewal = useRef<{ readonly voiceAgentId: string; readonly partyNumber: string } | null>(null);
  useEffect(() => {
    renewal.current = armed === null ? null : { voiceAgentId: voiceAgent.voiceAgentId, partyNumber: armed.partyNumber };
  });
  const armedSince = armed?.view.waitingSince ?? null;
  const armedNumber = armed?.partyNumber ?? null;
  const refetchProgress = progress.refetch;
  useEffect(() => {
    if (armedNumber === null || armedSince === null || waitingUntil === null) return;
    const delay = renewDelayOf(armedSince, waitingUntil, Date.now());
    if (delay === null) return;
    const timer = setTimeout(() => {
      gateway.renewVoiceTestCallArm(voiceAgent.voiceAgentId, armedNumber).then(
        (renewed) => {
          if (renewal.current?.partyNumber === armedNumber) setWaitingUntil(renewed.waitingUntil);
        },
        (error: unknown) => {
          if (!(error instanceof ApiError) || error.code !== "test_call_arm_not_found") return;
          // The arm is gone: a call took it (the read finds the call) or it lapsed (arm afresh).
          void refetchProgress().then((read) => {
            if (read.data === undefined || read.data.state === "idle") setExpired(true);
          });
        },
      );
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [gateway, voiceAgent.voiceAgentId, armedNumber, armedSince, waitingUntil, refetchProgress]);

  // plan-015 TC1: released when the panel closes or the page goes away (best effort; see `releaseArm`).
  useEffect(() => {
    const onPageHide = (): void => {
      const held = renewal.current;
      if (held !== null) releaseArm(gateway, held.voiceAgentId, held.partyNumber);
    };
    globalThis.addEventListener("pagehide", onPageHide);
    return () => {
      globalThis.removeEventListener("pagehide", onPageHide);
      onPageHide();
    };
  }, [gateway]);

  // The live transcript, once the call exists; a new call starts a new transcript.
  const conversationId = view?.conversationId ?? null;
  useEffect(() => {
    if (conversationId === null || subscribedTo.current === conversationId) return;
    subscription.current?.close();
    subscribedTo.current = conversationId;
    subscription.current = gateway.streamConversationTranscript(conversationId, (frame) => {
      if (frame.event === CONFIRMATION_CODE_SHOWN_EVENT) {
        // plan-029: the code this test would have sent by text message; anything but 4 to 8 digits is not drawn.
        const code = consoleCodeOf(frame.data);
        if (code === null) return;
        setStream((held) =>
          held !== null && held.conversationId === conversationId ? { ...held, code } : { conversationId, line: [], code },
        );
        return;
      }
      if (frame.event === "conversation.event") {
        // plan-021: a pass between VoiceAgents, drawn where it was heard; any other event is not drawn here.
        const event = voiceAgentPassFrameOf(frame.data);
        if (event === null) return;
        setStream((held) => {
          const same = held !== null && held.conversationId === conversationId;
          const line = same ? held.line : [];
          const code = same && held.code !== undefined ? { code: held.code } : {};
          return { conversationId, line, arrival: [...(same ? (held.arrival ?? []) : []), { event, afterLine: line.length }], ...code };
        });
        return;
      }
      if (frame.event !== "turn.final" && frame.event !== "turn.partial") return;
      const data = frame.data as { speaker?: unknown; text?: unknown };
      if (typeof data.speaker !== "string" || typeof data.text !== "string") return;
      const text = data.text;
      const speaker = data.speaker === "assistant" ? "assistant" : data.speaker === "caller" ? "caller" : "system";
      const next: TranscriptLineView = { speaker, text, partial: frame.event === "turn.partial" };
      setStream((held) =>
        held !== null && held.conversationId === conversationId
          ? { ...held, line: [...held.line, next] }
          : { conversationId, line: [next] },
      );
    });
  }, [conversationId, gateway]);

  const place = useMutation({
    mutationFn: (request: {
      readonly sent: string;
      readonly direction: TestCallDirection;
      readonly languageCode: string;
      readonly surveyArm?: SurveyArmView;
    }) =>
      gateway.startVoiceTestCall({
        voiceAgentId: voiceAgent.voiceAgentId,
        direction: request.direction,
        partyNumber: request.sent,
        languageCode: request.languageCode,
        ...(request.surveyArm === undefined ? {} : { surveyArm: request.surveyArm }),
      }),
    onSuccess: (started, request) => {
      // An arm after a finished call keeps that call's line on screen until the next call arrives.
      setLastEnded(view !== null && view.state === "ended" ? view : null);
      // plan-015 TC1: a different number armed in place of a waiting one releases the old arm.
      const held = renewal.current;
      if (held !== null && held.partyNumber !== request.sent) releaseArm(gateway, held.voiceAgentId, held.partyNumber);
      setPlaced({ view: started, partyNumber: request.sent, direction: request.direction });
      setWaitingUntil(started.waitingUntil);
      setMessage(null);
      setUnavailable(null);
      setExpired(false);
      setRefusedFor(null);
    },
    onError: (error: unknown, request) => {
      setRefusedFor(request.sent);
      // ST-TT: "I call the agent" on a trunk number while WeTalk takes no calls through phone systems.
      if (error instanceof TestCallUnavailableError || (error instanceof ApiError && error.code === "sip_trunk_inbound_off")) {
        setUnavailable(() => (apiErrorText(error, "")));
        setMessage(null);
        return;
      }
      setUnavailable(null);
      setMessage(() => (error instanceof Error ? apiErrorText(error, "") : portalText("operations", "the_test_could_not_start_nothing_was_charged_56e27f2e")));
    },
  });

  const end = useMutation({
    mutationFn: () => {
      if (conversationId === null) throw new Error(portalText("operations", "there_is_no_call_to_end_yet_ee1c30f9"));
      return gateway.endVoiceTestCall(conversationId);
    },
    onSuccess: () => {
      void progress.refetch();
    },
    onError: (error: unknown) => {
      setMessage(() => (error instanceof Error ? apiErrorText(error, "") : portalText("operations", "that_call_could_not_be_ended_hang_up_on_your_phone_fed24fa0")));
    },
  });

  // This call's streamed lines; while an arm waits (no conversation yet) the last call's stay.
  const line =
    stream === null ? [] : conversationId === null || stream.conversationId === conversationId ? stream.line : [];
  const spoken = line.length > 0 ? line : (view?.transcript ?? []);
  const voiceAgentPassArrival =
    line.length > 0 && stream !== null && stream.conversationId === conversationId ? (stream.arrival ?? []) : [];

  return {
    state,
    view,
    direction: placed?.direction ?? null,
    partyNumber: placed?.partyNumber ?? null,
    conversationId,
    line: spoken,
    voiceAgentPassArrival,
    consoleCode: stream !== null && stream.conversationId === conversationId ? (stream.code ?? null) : null,
    expired,
    lastEnded: conversationId === null ? lastEnded : null,
    message,
    unavailable,
    pending: place.isPending || end.isPending,
    refusedFor,
    start: (partyNumber, direction, languageCode, surveyArm) => {
      if (place.isPending) return;
      place.mutate({ sent: normalisedPhone(partyNumber), direction, languageCode, ...(surveyArm === undefined ? {} : { surveyArm }) });
    },
    hangUp: () => {
      end.mutate();
    },
  };
}
